import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';

function Header({ onSearchChange, showFitViewButton = true, fitView, viewMode, onViewModeChange }) {
    const { theme, setTheme } = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const [query, setQuery] = useState(searchParams.get('q') || '');

    useEffect(() => {
        setQuery(searchParams.get('q') || '');
    }, [searchParams]);

    const handleQueryChange = (e) => {
        const newQuery = e.target.value;
        setQuery(newQuery);
        if (onSearchChange) {
            onSearchChange(newQuery);
        }
    };

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        if (query.trim()) {
            navigate(`/search?q=${query.trim()}`);
        }
    };

    return (
        <header className="relative w-full bg-white/20 dark:bg-gray-900/40 backdrop-blur-xl shadow-lg flex-shrink-0 z-20 overflow-hidden border-b border-white/20 dark:border-gray-700" style={{ height: '90px', minHeight: '90px' }}>
            {/* 오로라 효과 */}
            <div className="absolute top-[-80px] left-[-100px] w-96 h-96 bg-blue-300/50 dark:bg-blue-500/30 rounded-full filter blur-3xl animate-pulse"></div>
            <div className="absolute bottom-[-100px] right-[-80px] w-80 h-80 bg-purple-300/50 dark:bg-purple-500/30 rounded-full filter blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>

            <div className="z-10 max-w-7xl py-1 px-10 flex justify-between items-center h-full">
                {/* 로고 */}
                <Link to="/">
                    <div>
                        <h1
                            className="text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white animate-fade-in-up"
                            style={{ textShadow: '1px 1px 15px rgba(255, 255, 255, 0.1)' }}
                        >
                            With AI
                        </h1>
                        <p
                            className="text-base text-gray-600 dark:text-gray-300 mt-1 animate-fade-in-up"
                            style={{ animationDelay: '0.2s' }}
                        >
                            다양한 AI 서비스를 찾아보세요
                        </p>
                    </div>
                </Link>

                {/* 컨트롤 + 검색창 */}
                <div className="absolute top-4 right-4 z-10 flex items-center space-x-3">
                    <form onSubmit={handleSearchSubmit}>
                        <input
                            type="text"
                            placeholder="서비스, 뉴스 검색..."
                            value={query}
                            onChange={handleQueryChange}
                            className="w-72 p-3 rounded-full bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 backdrop-blur-sm transition-all text-gray-800 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 shadow-sm"
                        />
                    </form>

                    {/* 홈 버튼 */}
                    <Link
                        to="/"
                        className="w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-transform duration-200 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-gray-200 dark:border-gray-700 hover:scale-110"
                        aria-label="Go to Home"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                    </Link>

                    {/* Fit View 버튼 */}
                    {showFitViewButton && (
                        <button
                            onClick={() => fitView({ padding: 0.4, duration: 800 })}
                            className="w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-transform duration-200 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-gray-200 dark:border-gray-700 hover:scale-110"
                            aria-label="Fit view"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1v4m0 0h-4m4 0l-5-5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 0h-4m4 0l-5 5" /></svg>
                        </button>
                    )}

                    {/* View Mode Toggle 버튼 */}
                    {onViewModeChange && (
                        <button
                            onClick={() => onViewModeChange(viewMode === 'mindmap' ? 'grid' : 'mindmap')}
                            className="w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-transform duration-200 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-gray-200 dark:border-gray-700 hover:scale-110"
                            aria-label="Toggle View Mode"
                            title={viewMode === 'mindmap' ? "바둑판 보기로 전환" : "마인드맵 보기로 전환"}
                        >
                            {viewMode === 'mindmap' ? (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                </svg>
                            ) : (
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
                                </svg>
                            )}
                        </button>
                    )}

                    {/* 테마 토글 버튼 */}
                    <button
                        onClick={() => setTheme(t => (t === 'light' ? 'dark' : 'light'))}
                        className="w-12 h-12 flex items-center justify-center rounded-full shadow-lg transition-transform duration-200 bg-white/70 dark:bg-gray-800/70 backdrop-blur-md border border-gray-200 dark:border-gray-700 hover:scale-110"
                        aria-label="Toggle theme"
                    >
                        {theme === 'light' ? (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-gray-700 dark:text-gray-200" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" /></svg>
                        ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M12 12a5 5 0 100-10 5 5 0 000 10z" /></svg>
                        )}
                    </button>
                </div>
            </div>
        </header>
    );
}

export default Header;